import {useState} from "react";
import {ChromePicker} from "react-color";
import {TextField} from "@mui/material";
import {Grid} from "@mui/material";

type PickerDialogProps = {
    value: string;
    onClick: () => void;
    onChange: (color: string) => void;
};

/**
 * This component is a dialog from which we can choose color from a palette or enter a hex or rgb code.
 *
 * @param value the hex code of a color
 * @param onClick callback to handle closing the dialog
 * @param onChange callback to handle the change of the target
 */
const PickerDialog = ({value, onClick, onChange}: PickerDialogProps) => (
    <div style={{position: "relative"}}>
        <div style={{position: "absolute", zIndex: "2"}}>
            <div style={{position: "fixed", top: "0px", right: "0px", bottom: "0px", left: "0px"}} onClick={onClick} />
            <ChromePicker color={value ?? "white"} onChange={(color, _) => onChange(color.hex)} disableAlpha={true} />
        </div>
    </div>
);

type ColorBoxProps = {
    color: string;
    onClick: () => void;
    show?: boolean;
};

/**
 * This component is a box filled with the specified color and a gray border.
 *
 * @param color the hex code of a color
 * @param onClick callback to handle clicking on the box
 * @param show if its value is true, the box is displayed, otherwise it is not
 */
const ColorBox = ({color, onClick, show}: ColorBoxProps) => (
    <div
        style={{
            display: show ? "inline" : "none",
            width: "25px",
            height: "25px",
            marginRight: "10px",
            backgroundColor: color,
            borderStyle: "solid",
            borderColor: "gray",
            borderWidth: "2px",
            borderRadius: "3px",
        }}
        onClick={onClick}
    ></div>
);

type Props = {
    onChange: (color: string) => void;
    value?: string;
    disabled?: boolean;
    placeholder?: string;
    textColoring?: boolean;
    showColorBox?: boolean;
};

/**
 * This component is color picker to select a color from a palette or enter a color name or hex code to the input field.
 *
 * @param onChange callback to handle the change of the color
 * @param colorType this is an enum value, it shows which parameter of the domain the selected color should be set to
 * @param placeholder this text appears in the input field if no color is selected
 * @param value the hex code of a color
 * @param disabled if disabled, the value of the input field cannot be changed and the color picker cannot be opened
 * @param textColoring if its value is true, the text of the input field is also colored with the selected color
 * @param showColorBox if its value is true, a box filled with the selected color appears next to the input field
 */
const ColorPicker = ({
    onChange,
    placeholder = "",
    value,
    disabled = false,
    textColoring = false,
    showColorBox = true,
}: Props) => {
    const [showPicker, setShowPicker] = useState(false);

    return (
        <Grid container>
            <ColorBox color={value} onClick={() => setShowPicker(true)} show={showColorBox} />
            <TextField
                variant="standard"
                value={value ?? ""}
                placeholder={placeholder}
                disabled={disabled}
                onClick={() => setShowPicker(true)}
                onChange={(e) => {
                    onChange(e.target.value);
                }}
                InputProps={{style: {color: textColoring ? value : "black"}}}
            />
            {showPicker && !disabled && (
                <PickerDialog
                    value={value}
                    onClick={() => {
                        setShowPicker(false);
                    }}
                    onChange={(colorhex) => {
                        onChange(colorhex);
                    }}
                />
            )}
        </Grid>
    );
};

export default ColorPicker;
